import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import PropTypes from "prop-types";
import { Autocomplete, Stack, Pagination, ThemeProvider } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useNavigate } from "react-router-dom";
import useTableTheme from "constants/TableTheme";

function MRTDataTable({
  canSearch = true,
  onSearchChange,
  table,
  name,
  isSorted = true,
  enableTopToolbar = false,
  tablePage,
  setTablePage,
  entriesPerPage,
  setEntriesPerPage,
  totalLength,
  pagination,
  navigateRow,
  tableMaxHeight,
  tableMinHeight,
  setSortOrder,
  setSortBy,
  sortBy,
  sortOrder,
  entriesOption = ["10", "20", "30", "40", "50"],
  renderTopToolbarCustomActions,
  initialState,
}) {
  const navigate = useNavigate();
  const tableTheme = useTableTheme();

  const columns = useMemo(
    () =>
      table.columns.map((col) => ({
        accessorKey: col?.accessor ?? col?.accessorKey,
        header: col?.Header,
        size: col?.width,
        muiTableHeadCellProps: {
          align: col.align ?? "left",
          sx: { p: 0 },
        },
        muiTableBodyCellProps: {
          align: col.align ?? "left",
          sx: {
            backgroundColor: (row) =>
              row?.original?.isActive === false ? "#F8F8F8" : undefined,
            p: 0,
          },
        },
        Cell: col.Cell ?? undefined,
      })) || [],
    [table.columns],
  );

  const data = useMemo(() => table.rows || [], [table]);

  const defaultInitialState =
    sortBy && sortOrder
      ? {
          density: "compact",
          sorting: [{ id: sortBy, desc: sortOrder === "desc" }],
        }
      : {
          density: "compact",
        };

  const effectiveInitialState = initialState ?? defaultInitialState;

  const mrtHandler =
    sortBy && sortOrder
      ? {
          columns,
          data,
          enablePagination: false,
          enableSorting: isSorted,
          manualSorting: sortBy && sortOrder ? true : false,
          muiTableContainerProps: {
            sx: {
              boxShadow: "none",
              maxHeight: tableMaxHeight || "calc(100vh - 120px)",
              minHeight: tableMinHeight || "",
            },
          },
          muiTableBodyRowProps: ({ row }) => ({
            onClick: () => {
              if (navigateRow) navigate(`${navigateRow}/${row.original?.id}`);
            },
            sx: {
              cursor: "pointer",
              backgroundColor:
                row?.original?.isActive === false ? "#F8F8F8" : "",
              // "&:nth-of-type(odd)": {
              //   backgroundColor: "#FDEBED",
              // },
            },
          }),
          enableDensityToggle: false,
          enableStickyHeader: true,
          enableTopToolbar: enableTopToolbar,
          enableBottomToolbar: false,
          sortingFns: {
            alphanumeric: (rowA, rowB, columnId) =>
              rowA.getValue(columnId)?.localeCompare(rowB.getValue(columnId)),
          },
          state: effectiveInitialState,
          // Only use external sorting handler if manualSorting is true
          onSortingChange: (updater) => {
            const currentSort =
              sortBy && sortOrder
                ? [{ id: sortBy, desc: sortOrder === "desc" }]
                : [];

            const nextSort =
              typeof updater === "function" ? updater(currentSort) : updater;

            if (nextSort[0]) {
              setSortBy?.(nextSort[0].id);
              setSortOrder?.(nextSort[0].desc ? "desc" : "asc");
            } else {
              setSortBy?.("");
              setSortOrder?.("");
            }
          },
          muiTablePaperProps: {
            elevation: 0,
            sx: {
              width: "100%",
              borderRadius: "0",
              backgroundColor: "#fff !important",
              zIndex: 500,
            },
          },
          renderEmptyRowsFallback: () => (
            <Stack
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="14px"
              p={5}
            >
              No records to display
            </Stack>
          ),
          renderTopToolbarCustomActions: renderTopToolbarCustomActions,
        }
      : {
          columns,
          data,
          enablePagination: false,
          enableSorting: isSorted,
          manualSorting: sortBy && sortOrder ? true : false,
          muiTableContainerProps: {
            sx: {
              boxShadow: "none",
              maxHeight: tableMaxHeight || "calc(100vh - 120px)",
              minHeight: tableMinHeight || "",
            },
          },
          muiTableBodyRowProps: ({ row }) => ({
            onClick: () => {
              if (navigateRow) navigate(`${navigateRow}/${row.original?.id}`);
            },
            sx: {
              cursor: "pointer",
              backgroundColor:
                row?.original?.isActive === false ? "#F8F8F8" : "",
              // "&:nth-of-type(odd)": {
              //   backgroundColor: "#FDEBED",
              // },
            },
          }),
          initialState: {
            showGlobalFilter: true,
          },
          enableDensityToggle: false,
          enableStickyHeader: true,
          enableTopToolbar: enableTopToolbar,
          enableBottomToolbar: false,
          sortingFns: {
            alphanumeric: (rowA, rowB, columnId) =>
              rowA.getValue(columnId)?.localeCompare(rowB.getValue(columnId)),
          },
          state: effectiveInitialState,

          muiTablePaperProps: {
            elevation: 0,
            sx: {
              width: "100%",
              borderRadius: "0",
              backgroundColor: "#fff !important",
              zIndex: 500,
              paddingLeft: "6px",
            },
          },
          renderEmptyRowsFallback: () => (
            <Stack
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="14px"
              p={5}
            >
              No records to display
            </Stack>
          ),
          renderTopToolbarCustomActions: renderTopToolbarCustomActions,
        };

  const mrt = useMaterialReactTable(mrtHandler);

  return (
    <>
      {canSearch && (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}
        >
          <MDBox
            width="100%"
            ml="auto"
            display="flex"
            alignItems="center"
            justifyContent={name ? "space-between" : "flex-end"}
          >
            {name && <MDBox color="headingColor">{name}</MDBox>}
            <MDInput
              width="12rem"
              placeholder="Search..."
              size="small"
              onChange={(e) => {
                if (onSearchChange) {
                  onSearchChange(e);
                } else {
                  mrt.setGlobalFilter(e.target.value);
                }
              }}
            />
          </MDBox>
        </MDBox>
      )}

      <ThemeProvider theme={tableTheme}>
        <MaterialReactTable table={mrt} />
      </ThemeProvider>

      {pagination && data.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" mt={3} mx={2}>
          <Pagination
            count={Math.ceil(totalLength / entriesPerPage)}
            variant="outlined"
            shape="rounded"
            color="info"
            page={tablePage}
            onChange={(event, newPage) => setTablePage?.(newPage)}
            sx={{ ml: 2 }}
          />
          <MDBox display="flex" alignItems="center">
            <Autocomplete
              disableClearable
              value={entriesPerPage.toString()}
              options={entriesOption}
              onChange={(event, newValue) => {
                setEntriesPerPage?.(parseInt(newValue, 10));
              }}
              size="small"
              sx={{ width: "5rem" }}
              renderInput={(params) => <MDInput {...params} />}
            />
            <MDTypography variant="caption" color="dark">
              &nbsp;&nbsp;Entries per page
            </MDTypography>
          </MDBox>
        </Stack>
      )}
    </>
  );
}

MRTDataTable.propTypes = {
  canSearch: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  name: PropTypes.string,
  isSorted: PropTypes.bool,
  tablePage: PropTypes.number,
  setTablePage: PropTypes.func,
  entriesPerPage: PropTypes.number,
  setEntriesPerPage: PropTypes.func,
  totalLength: PropTypes.number,
  pagination: PropTypes.bool,
  navigateRow: PropTypes.string,
  tableMaxHeight: PropTypes.string,
  rowWidth: PropTypes.any,
  setSortOrder: PropTypes.func,
  setSortBy: PropTypes.func,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  manualSorting: PropTypes.bool,
  manualPagination: PropTypes.bool,
  entriesOption: PropTypes.array,
};

export default MRTDataTable;
